// Mark — the app's brand glyph: four rounded petals in a diamond (the
// reference design system's mark), with ONE motion grammar for "the app is at
// work": idle (still), thinking (spins), pipeline (petals fill stage by stage
// while it spins), success (a green pulse, then rests).
//
// Not AiMarker (provenance: "this was made by AI") and not Spinner (a generic
// wait): the Mark is identity + the app's own processing states. Pure: props
// in, SVG out. All motion is CSS on the loop tokens and is frozen by the
// global reduced-motion reset, so the state must also be said in words.
//
// a11y: decorative by default (aria-hidden), because it should sit beside a
// visible state word. Pass `label` only when the mark alone carries meaning;
// it then becomes role="img" with that name.

import "./Mark.css";

// Stages completed while a pipeline runs. The petals map to fixed stages in
// order: top = ingest, left = verify, right = curate, bottom = export. Four
// done is not a running pipeline; show `success` instead.
type MarkStagesDone = 0 | 1 | 2 | 3;

export type MarkState =
    | { readonly type: "idle" }
    | { readonly type: "thinking" }
    | { readonly type: "pipeline"; readonly stagesDone: MarkStagesDone }
    | { readonly type: "success" };

// On the icon scale (16 / 20 / 24), so it aligns with adjacent glyphs.
type MarkSize = "sm" | "md" | "lg";

export type MarkProps = {
    readonly state?: MarkState;
    readonly size?: MarkSize;
    // Accessible name, caller-resolved (Lingui). Omit when a visible word
    // beside the mark already says the state.
    readonly label?: string;
    // Layout and tone hook: petals paint in currentColor.
    readonly className?: string;
};

// Petal origins on a 22-unit grid (8-unit petals), in stage order.
const PETALS: readonly { readonly x: number; readonly y: number }[] = [
    { x: 7, y: 0 },
    { x: 0, y: 7 },
    { x: 14, y: 7 },
    { x: 7, y: 14 },
];

const IDLE: MarkState = { type: "idle" };

const isPending = (state: MarkState, stage: number): boolean =>
    state.type === "pipeline" && stage >= state.stagesDone;

export const Mark = ({
    state = IDLE,
    size = "md",
    label,
    className,
}: MarkProps) => (
    <svg
        viewBox="0 0 22 22"
        focusable="false"
        className={`bilda-mark bilda-mark--${size} bilda-mark--${state.type}${
            className ? ` ${className}` : ""
        }`}
        role="img"
        aria-label={label}
        aria-hidden={label ? undefined : true}
    >
        {PETALS.map(({ x, y }, stage) => (
            <rect
                key={`${x}-${y}`}
                x={x}
                y={y}
                width={8}
                height={8}
                rx={3}
                className={
                    isPending(state, stage)
                        ? "bilda-mark__petal bilda-mark__petal--pending"
                        : "bilda-mark__petal"
                }
            />
        ))}
    </svg>
);
